import React, {useState, useEffect, useMemo} from "react";
import data from "../data/data";

function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("#home"); // to track active section

  // get full name
  const fullName = `${data[0]?.profile?.firstName} ${data[0]?.profile?.lastName}`;

  // useMemo to memoize the navItems array
  const navItems = useMemo(
    () => [
      {navName: "Home", link: "#home"},
      {navName: "About me", link: "#about"},
      {navName: "Experience", link: "#experience"},
      {navName: "Skills", link: "#skills"},
      {navName: "Education", link: "#education"},
      {navName: "Portfolio", link: "#portfolio"},
      {navName: "Contact", link: "#contact"}
    ],
    []
  );

  const scrollToSection = id => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({behavior: "smooth"});
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Function to detect active section in the viewport
  useEffect(() => {
    const sections = navItems.map(item => document.querySelector(item.link));
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(`#${entry.target.id}`);
          }
        });
      },
      {threshold: 0.6} // Adjust the threshold for when the section is considered active
    );

    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach(section => {
        if (section) observer.unobserve(section);
      });
    };
  }, [navItems]);

  return (
    <nav
      className={`py-4 h-20 border-bottom-500 z-10 w-full lg:fixed transition-colors duration-300 ${
        isScrolled ? "bg-secondary" : "bg-inherit"
      }`}
      id="topnav"
    >
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="flex items-center justify-between">
          <a
            href="/"
            className={`flex items-center font-lilita tracking-wider text-2xl transition-colors duration-300 ${
              isScrolled ? "text-white" : "text-indigo-600"
            }`}
          >
            {fullName}
          </a>
          <div className="lg:hidden">
            <button
              className="text-secondary focus:outline-none"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="mobile nav"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={
                    isMobileMenuOpen
                      ? "M6 18L18 6M6 6l12 12"
                      : "M4 6h16M4 12h16m-7 6h7"
                  }
                />
              </svg>
            </button>
          </div>
          <div className="hidden lg:flex flex-grow justify-center">
            <ul className="flex flex-row lg:space-x-6 space-y-0">
              {navItems.map((nav, i) => (
                <li key={i}>
                  <a
                    href={nav.link}
                    className={`text-md font-montserrat font-semibold transition-colors duration-300 relative inline-block ${
                      isScrolled
                        ? "text-gray300 hover:text-white"
                        : "text-black"
                    } hover:text-indigo-600 ${
                      activeSection === nav.link ? "active-nav" : ""
                    }`} // apply the active-nav class when the section is active
                    onClick={() => scrollToSection(nav.link)}
                  >
                    {nav.navName}
                    <span
                      className={`absolute left-0 bottom-0 w-full h-[4px] rounded-md ${
                        isScrolled ? "bg-white" : "bg-indigo-600"
                      } transition-transform duration-300 transform ${
                        activeSection === nav.link ? "scale-x-100" : "scale-x-0"
                      }`}
                    ></span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="hidden lg:flex items-center justify-center">
            <a
              href="/Resume.pdf"
              download="Muhammad Taha Resume.pdf"
              className={`rounded-full font-semibold font-montserrat shadow-xs transition-all duration-300 py-3 px-6 text-sm hover:bg-indigo-700 inline-flex items-center justify-center ${
                isScrolled
                  ? "bg-white text-indigo-600 hover:text-white"
                  : "bg-indigo-600 text-white"
              }`}
            >
              Download Resume
              <img
                src={
                  isScrolled
                    ? data[0].nav.secondaryDownloadSvg
                    : data[0].nav.downloadSvg
                }
                alt="download resume"
                className="ml-2"
                width="16px"
              />
            </a>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      <div
        className={`lg:hidden transition-all duration-600 ${
          isMobileMenuOpen ? "max-h-screen" : "max-h-0 overflow-hidden"
        }`}
      >
        <div className="bg-secondary pb-6">
          <ul className="flex flex-col font-lilita items-center space-y-4 py-4 mt-4">
            {navItems.map((nav, i) => (
              <li key={i}>
                <a
                  href={nav.link}
                  className="text-white text-sm font-medium font-lilita hover:text-white transition-colors duration-300"
                  onClick={() => {
                    scrollToSection(nav.link);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {nav.navName}
                </a>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-center">
            <a
              href="/Resume.pdf"
              download="Muhammad Taha Resume.pdf"
              className="rounded-full font-semibold font-montserrat shadow-xs transition-all duration-300 py-3 px-6 text-sm hover:bg-indigo-700 inline-flex items-center justify-center bg-white text-secondary
              "
            >
              Download Resume
              <img
                src={data[0]?.nav?.secondaryDownloadSvg}
                alt="resume download"
                className="ml-2"
                width="16px"
              />
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Header;
